/**
 * PendingDiffBar — sticky action bar shown above the input box whenever
 * there's a pending diff awaiting user approval.
 *
 * Why it exists: long assistant text + diff card pushed Apply / Reject
 * buttons off-screen, forcing users to scroll to confirm changes. The
 * inline DiffPreview still renders for review (checkboxes, change list,
 * before/after) — but the action buttons also surface here at viewport
 * bottom so they're always one click away.
 *
 * Behavior:
 *   - Only renders when a message in the transcript has diff.status ===
 *     'pending' AND at least one change is selected. Nothing shown
 *     during streaming (the model is still proposing) or after applied/
 *     rejected (user already decided).
 *   - Apply / Reject reuse the same handlers wired in DiffPreview by
 *     dispatching to chatStore + the office.ts applier — no duplicate
 *     logic, just a second mount point of the same actions.
 *   - Hidden by default; pure CSS slide-up on appearance keeps it calm.
 */

import { Check, Loader2, X } from 'lucide-react';
import { useChatStore } from '@/stores/chatStore';
import { applyDiffMessage } from '@/services/applyFlow';
import { confirmChanges } from '@/services/chat';
import { extractErrorMessage } from '@/services/api';
import type { ChangeDecision, UIMessage } from '@/types/chat';

interface Props {
  conversationId: string | null;
}

// findActiveDiffMessage walks newest-first and returns the most recent
// message whose diff is awaiting user action. Streaming diffs are
// excluded — the model may still merge / drop entries before the final
// set arrives.
function findActiveDiffMessage(messages: UIMessage[]): UIMessage | null {
  for (let i = messages.length - 1; i >= 0; i--) {
    const m = messages[i];
    if (!m.diff) continue;
    if (m.diff.status === 'pending' || m.diff.status === 'applying') return m;
  }
  return null;
}

export function PendingDiffBar({ conversationId }: Props) {
  // Narrow selectors — the whole-store subscription re-rendered the bar
  // on every SSE token. Deriving the active-diff message INSIDE the
  // selector keeps re-renders limited to when that message's object
  // identity actually changes (zustand compares with Object.is); the
  // action selectors are stable references and never re-render.
  const message = useChatStore((s) => findActiveDiffMessage(s.messages));
  const setDiffStatus = useChatStore((s) => s.setDiffStatus);
  const setApplyProgress = useChatStore((s) => s.setApplyProgress);
  const stopAutoBuild = useChatStore((s) => s.stopAutoBuild);

  if (!message || !message.diff) return null;
  // Never show the action bar for an empty diff — nothing to apply.
  if (message.diff.changes.length === 0) return null;

  const { status, changes, selected } = message.diff;
  const selectedCount = changes.filter((c) => selected[c.id]).length;
  const isApplying = status === 'applying';

  const onApply = async () => {
    await applyDiffMessage(conversationId, message, {
      onProgress: (done, total, label) => setApplyProgress({ messageId: message.id, done, total, label }),
    });
    setApplyProgress(null);
  };

  const onRejectAll = async () => {
    stopAutoBuild();
    if (!conversationId) {
      setDiffStatus(message.id, 'rejected', []);
      return;
    }
    const decisions: ChangeDecision[] = changes.map((c) => ({
      change_id: c.id,
      outcome: 'rejected',
    }));
    setDiffStatus(message.id, 'applying');
    try {
      await confirmChanges({
        conversation_id: conversationId,
        message_id: message.id,
        decisions,
      });
    } catch (err) {
      console.warn('confirmChanges failed:', extractErrorMessage(err));
    }
    setDiffStatus(message.id, 'rejected', []);
  };

  return (
    <div className="border-t border-primary/30 bg-primary/5 px-3 py-2 dark:bg-primary/10 animate-in fade-in slide-in-from-bottom-2 duration-200">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 text-xs">
          <div className="h-2 w-2 rounded-full bg-primary animate-pulse" />
          <span className="font-medium">
            {changes.length} change{changes.length === 1 ? '' : 's'} ready
          </span>
          {selectedCount !== changes.length && (
            <span className="text-text-secondary">
              · {selectedCount} selected
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={onRejectAll}
            disabled={isApplying}
            className="btn-ghost text-xs px-2 py-1"
          >
            <X className="mr-1 h-3.5 w-3.5" /> Reject
          </button>
          <button
            type="button"
            onClick={onApply}
            disabled={isApplying || selectedCount === 0}
            className="btn-primary text-xs px-3 py-1.5"
          >
            {isApplying ? (
              <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />
            ) : (
              <Check className="mr-1 h-3.5 w-3.5" />
            )}
            Apply ({selectedCount})
          </button>
        </div>
      </div>
    </div>
  );
}
